import React, { useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  TopToolbar,
  FilterButton,
  SelectInput,
  FunctionField,
  Filter,
  TextInput,
} from 'react-admin';
import { Button, Box, Typography, Grid } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import NewReferencePriceDialog from './new_reference_price_dialog';
import Cascader from '../components/cascader';
import { Division } from '../components/division-input';
import cascaderOptions, { DivisionUtil } from '@pansy/china-division';

import { RefPriceOrder } from '../../models/response/RefPrice';
import EditReferencePriceDialog from './edit_reference_price_dialog';

const divisionUtil = new DivisionUtil(cascaderOptions);
// 货车类型选项
const truckTypeOptions = [
  { id: 0, name: '平板' },
  { id: 1, name: '高栏' },
  { id: 2, name: '厢式' },
  { id: 3, name: '自卸' },
  { id: 4, name: '冷藏' },
  { id: 5, name: '爬梯' },
  { id: 6, name: '集装箱' },
  { id: 7, name: '高低板' },
];

// 货车长度选项
const truckLengthOptions = [
  { id: 0, length: 4.2, name: '4米2' },
  { id: 1, length: 6.8, name: '6米6' },
  { id: 2, length: 8.7, name: '8米7' },
  { id: 3, length: 9.6, name: '9米6' },
  { id: 4, length: 13, name: '13米' },
  { id: 5, length: 13.75, name: '13米75' },
  { id: 6, length: 17.5, name: '17米5' },
];

// 认证状态选项
const verifyStatusOptions = [
  { id: 0, name: '订单' },
  { id: 1, name: '调度' },
];

// 筛选器组件
const VerifyListFilters = (props: any) => {
  const {
    onSourceChange,
    onFromChange,
    onToChange,
  } = props;

  return (
    <Filter {...props}>
      <SelectInput
        source="source"
        label="数据源"
        defaultValue={0}
        choices={verifyStatusOptions}
        alwaysOn={true}
        onChange={(value) => onSourceChange(Number(value.target.value))}
      />
      <TextInput 
        source="from" 
        label="起点" 
        alwaysOn 
        onChange={(e) => {
          const value = e.target.value;
          if (value) {
            // 这里简化处理，实际可能需要更复杂的地址解析
            onFromChange({ province: value, city: '', district: '' });
          } else {
            onFromChange('');
          }
        }}
        defaultValue=''
      />
      <TextInput 
        source="to" 
        label="终点" 
        alwaysOn 
        onChange={(e) => {
          const value = e.target.value;
          if (value) {
            // 这里简化处理，实际可能需要更复杂的地址解析
            onToChange({ province: value, city: '', district: '' });
          } else {
            onToChange("");
          }
        }}
        defaultValue=''
      />
    </Filter>
  );
};

// 工具栏
const ListActions = ({
  onAddClick,
  onResetFilters,
  filters
}: {
  onAddClick: () => void;
  onResetFilters: () => void;
  filters: React.ReactNode;
}) => (
  <TopToolbar>
    <FilterButton filters={filters ? [filters] : undefined} />
    <Button
      onClick={onResetFilters}
      variant="outlined"
      color="primary"
      size="small"
      sx={{ marginLeft: 2 }}
    >
      重置筛选
    </Button>
    <Button
      onClick={onAddClick}
      startIcon={<AddIcon />}
      variant="contained"
      color="primary"
      size="small"
      sx={{ marginLeft: 2 }}
    >
      添加参考价格
    </Button>
  </TopToolbar>
);

const ReferncePriceList = () => {
  const [editPriceDialogOpen, setEditPriceDialogOpen] = useState(false);
  const [newPriceDialogOpen, setNewPriceDialogOpen] = useState(false);
  const [sourceType, setSourceType] = useState<number>(0);
  const [fromDivision, setFromDivision] = useState<Division | undefined>(undefined);
  const [toDivision, setToDivision] = useState<Division | undefined>(undefined);
  const [editingRefPrice, setEditingRefPrice] = useState<RefPriceOrder | undefined>(undefined);

  const handleOpenNewPriceDialog = () => {
    setEditingRefPrice(undefined); // 清空编辑状态，表示新增
    setNewPriceDialogOpen(true);
  };

  const handleCloseNewPriceDialog = () => {
    setNewPriceDialogOpen(false);
  };

  const handleCloseEditPriceDialog = () => {
    setEditPriceDialogOpen(false);
    setEditingRefPrice(undefined); // 关闭弹窗时清空编辑状态
  };
  
  // 处理编辑参考价格
  const handleEditRefPrice = (record: RefPriceOrder) => {
    setEditingRefPrice(record); // 设置当前编辑的参考价格
    setEditPriceDialogOpen(true); // 打开弹窗
  };

  // 重置筛选条件
  const handleResetFilters = () => {
    setSourceType(0); // 重置数据源为"订单(0)"
    setFromDivision(undefined); // 清空起点
    setToDivision(undefined); // 清空终点
  };

  // 构建筛选条件对象
  const buildFilterObject = () => {
    const filter: Record<string, any> = { source: sourceType };

    // 添加起点筛选条件
    if (fromDivision) {
      filter.from = `${fromDivision.province}${fromDivision.city}${fromDivision.district}`;
    }

    // 添加终点筛选条件
    if (toDivision) {
      filter.to = `${toDivision.province}${toDivision.city}${toDivision.district}`;
    }

    return filter;
  };

  return (
    <>
      <List
        actions={<ListActions
          onAddClick={handleOpenNewPriceDialog}
          onResetFilters={handleResetFilters}
          filters={<VerifyListFilters
            onSourceChange={setSourceType}
            onFromChange={setFromDivision}
            onToChange={setToDivision}
            fromDivision={fromDivision}
            toDivision={toDivision}
          />}
        />}
        filters={<VerifyListFilters
          onSourceChange={setSourceType}
          onFromChange={setFromDivision}
          onToChange={setToDivision}
          fromDivision={fromDivision}
          toDivision={toDivision}
        />}
        sort={{ field: 'createdAt', order: 'DESC' }}
        perPage={10}
        filter={buildFilterObject()}
        filterDefaultValues={{ source: 0 }}
      >
        <Datagrid bulkActionButtons={false}>
          {/* 当数据源是订单(sourceType=0)时才显示订单ID列 */}
          {sourceType === 0 && <TextField source="order" label="订单ID" />}
          <TextField source="from" label="起点" />
          <TextField source="to" label="终点" />
          <TextField source="price" label="参考价格(元)" />
          <FunctionField
            label="车型"
            render={(record: RefPriceOrder) => {
              if (record.truck_type === undefined) return '';
              const truckType = truckTypeOptions.find(option => option.id === record.truck_type);
              return truckType ? truckType.name : '';
            }}
          />
          <FunctionField
            label="车长"
            render={(record: RefPriceOrder) => {
              if (record.truck_length === undefined) return '';
              const truckLength = truckLengthOptions.find(option => option.id === record.truck_length);
              return truckLength ? truckLength.name : '';
            }}
          />
          <FunctionField
            label="直线距离(km)"
            render={(record: RefPriceOrder) =>
              record.distance_linear ? (record.distance_linear / 1000).toFixed(2) : ''
            }
          />
          <FunctionField
            label="时间"
            render={(record: RefPriceOrder) =>
              record.time ? new Date(record.time).toLocaleString('zh-CN') : ''
            }
          />
          {/* 添加修改按钮，只在数据源类型为调度(sourceType=1)时显示 */}
          {sourceType === 1 && (
            <FunctionField
              label="操作"
              render={(record: RefPriceOrder) => (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => handleEditRefPrice(record)}
                >
                  修改
                </Button>
              )}
            />
          )}
        </Datagrid>

      </List>

      <NewReferencePriceDialog
        open={newPriceDialogOpen}
        onClose={handleCloseNewPriceDialog}
      />

      <EditReferencePriceDialog
        open={editPriceDialogOpen}
        onClose={handleCloseEditPriceDialog}
        refPriceOrder={editingRefPrice}
      />
    </>
  );
};

export default ReferncePriceList;
